import React, { useEffect, useMemo, useState } from 'react';
import { pitchSubTypeColor, pitchTypeDisplayName, HeadingChart, BatImpactChart, pitchTypeOrSubType, Checkbox, Button, number } from 'yankees-react-utils';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'react-bootstrap';
import ReportCard from '../../../../components/ReportCard';
import {
    COUNT_BUCKET,
    matchesCountBucket,
    findBatTrackingEllipse,
    buildLeagueHeadingByPitchType,
} from '../batTrackingCountBucket';

const PITCHER_OVERVIEW_COUNT_BUCKET = COUNT_BUCKET.OVERALL;

const StyledCheckboxWrapper = styled.div`
    .nyy-checkbox-radio-container {
        display: flex;
    }
    & input[type="checkbox"]:checked + label::before {
        background-color: ${(props) => props.$color} !important;
        border-color: ${(props) => props.$color} !important;
    }
    & input[type="checkbox"] + label::before {
        border-radius: ${(props) => (props.$square ? '0' : '100%')};
    }
`;

const buildPitchTypesSeries = (
    pitches,
    repertoire,
) => repertoire.map((r) => {
    const pitchType = pitchTypeDisplayName(r);
    return {
        id: pitchType,
        name: pitchType,
        color: pitchSubTypeColor({
            type: r.fxtype,
            fxsubtype: r.fxsubtype,
        }),
        data: pitches.filter((p) => p.fxtype === r.fxtype && p.fxsubtype === r.fxsubtype)
            .map((p) => ({
                fxtype: p.fxtype,
                fxsubtype: p.fxsubtype,
                gameid: p.gameid,
                eventseq: p.eventseq,
                pitchseq: p.pitchseq,
                hbat: p.hbat,
                vbat: p.vbat,
                heading: p.heading,
                inz: p.inz,
                marker: {
                    symbol: p.inz ? 'square' : 'circle',
                    radius: 5,
                    lineWidth: 1,
                    lineColor: '#fff',
                    states: { hover: { radiusPlus: 10, lineWidth: 2, lineColor: '#fff' } },
                },
            })),
    };
});

const pitchOrders = [10, 9, 8, 33, 3, 34, 4, 7, 2];
const getPitchOrderValue = (pt) => {
    const index = pitchOrders.indexOf(pt);

    return index > -1 ? index : 100 + pt;
};

const getPitchTypeSelection = (pitchType, inz) => ({
    fxtype: pitchType.fxtype,
    fxsubtype: pitchType.fxsubtype,
    inz,
});

const getDefaultOverviewSelectedPitchTypes = (repertoire) => repertoire
    .filter((r) => r.fxtype > 1)
    .map((r) => getPitchTypeSelection(r, null));

const getPitchTypeTrackedSwings = (pitches, pitchType, inz) => (
    pitches.find((p) => p.fxtype === pitchType.fxtype
        && p.fxsubtype === pitchType.fxsubtype
        && p.inz === inz
        && matchesCountBucket(p.countbucket, PITCHER_OVERVIEW_COUNT_BUCKET))?.trackedswings ?? 0
);

export default function BatTracking({ pitches, isLhb, isLhp, repertoire, hbatVbatEllipses }) {
    const { t: translate } = useTranslation();
    const [selectedPitchTypes, setSelectedPitchTypes] = useState(() => getDefaultOverviewSelectedPitchTypes(repertoire));

    useEffect(() => {
        setSelectedPitchTypes(getDefaultOverviewSelectedPitchTypes(repertoire));
    }, [repertoire]);

    const orderedPitchTypes = repertoire.filter((r) => r.fxtype > 1)
        .sort((a, b) => getPitchOrderValue(b.fxsubtype || b.fxtype) - getPitchOrderValue(a.fxsubtype || a.fxtype));
    const filteredOrderedPitchTypes = repertoire
        .filter((r) => r.fxtype > 1 && selectedPitchTypes.some((spt) => spt.fxtype === r.fxtype && spt.fxsubtype === r.fxsubtype))
        .sort((a, b) => getPitchOrderValue(b.fxsubtype || b.fxtype) - getPitchOrderValue(a.fxsubtype || a.fxtype));
    const filteredPitches = pitches.filter((p) => matchesCountBucket(p.countbucket, PITCHER_OVERVIEW_COUNT_BUCKET)
        && selectedPitchTypes.some((spt) => spt.fxtype === p.fxtype && spt.fxsubtype === p.fxsubtype && spt.inz === p.inz));
    const series = buildPitchTypesSeries(filteredPitches, filteredOrderedPitchTypes);
    const headingSeries = series.map((s) => ({
        ...s,
        data: s.data.filter((x) => x.heading !== null).map((d) => ({
            ...d,
            x: d.heading,
            y: filteredOrderedPitchTypes.findIndex(((r) => r.fxtype === d.fxtype && r.fxsubtype === d.fxsubtype)),
        })),
    }));
    const batImpactSeries = series.filter((x) => x.hbat !== null && x.vbat !== null).map((s) => ({
        ...s,
        data: s.data.map((d) => ({
            ...d,
            x: d.hbat,
            y: d.vbat,
        })),
    }));

    const onPitchTypeClick = (pitchType) => {
        setSelectedPitchTypes((current) => {
            if (current.some((spt) => spt.fxtype === pitchType.fxtype && spt.fxsubtype === pitchType.fxsubtype && spt.inz === pitchType.inz)) {
                return current.filter((spt) => spt.fxtype !== pitchType.fxtype || spt.fxsubtype !== pitchType.fxsubtype || spt.inz !== pitchType.inz);
            }
            return [...current, pitchType];
        });
    };

    const headingTooltipFormatter = (s, p) => `<b>${s.name}${p.inz ? ' - InZ' : ''}</b><br />Heading: ${number(p.heading, 1)}`;
    const batImpactTooltipFormatter = (s, p) => `<b>${s.name}${p.inz ? ' - InZ' : ''}</b><br />HBat: ${number(p.hbat, 1)}<br />VBat: ${number(p.vbat, 1)}`;
    const pitchTypeLeagueAverages = useMemo(() => filteredOrderedPitchTypes
        .map((spt) => {
            const match = findBatTrackingEllipse(hbatVbatEllipses, {
                isLhb, isLhp, fxtype: spt.fxtype, fxsubtype: spt.fxsubtype, countBucket: PITCHER_OVERVIEW_COUNT_BUCKET,
            });
            if (match?.centerx == null) {
                return null;
            }
            return {
                fxtype: spt.fxtype,
                fxsubtype: spt.fxsubtype,
                x: match.centerx,
                y: match.centerz,
                minorRadius: match.minorrad,
                majorRadius: match.majorrad,
                angle: match.angle,
            };
        }).filter((x) => !!x), [hbatVbatEllipses, isLhp, isLhb, filteredOrderedPitchTypes]);

    const leagueHeadingByPitchType = useMemo(() => buildLeagueHeadingByPitchType(
        filteredOrderedPitchTypes,
        hbatVbatEllipses,
        { isLhb, isLhp, countBucket: PITCHER_OVERVIEW_COUNT_BUCKET },
    ), [filteredOrderedPitchTypes, hbatVbatEllipses, isLhb, isLhp]);

    return (
        <Row>
            <Col md={12}>
                <ReportCard title={translate('pitcher-overview.sections.bat-tracking')}>
                    {pitches.length ? (
                        <div className="py-2">
                            <div className="d-flex justify-content-around">
                                <div className="text-center">
                                    <div className="fs-4 fw-bold">{translate('pitcher-overview.sections.bat-tracking-timing')}</div>
                                    <HeadingChart
                                        seriesData={headingSeries}
                                        orderedPitchTypes={filteredOrderedPitchTypes.map((pt) => pt.fxsubtype || pt.fxtype)}
                                        tooltipFormatter={headingTooltipFormatter}
                                        showLeagueAverageEllipses
                                        leagueHeadingByPitchType={leagueHeadingByPitchType}
                                        disableJitter
                                        sizeMultiplier={0.8}
                                        min={-20}
                                        max={50}
                                    />
                                    <div className="d-flex justify-content-between ms-2 ps-5 pe-3">
                                        <div className="d-flex align-items-center">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                height="29px"
                                                viewBox="0 -960 960 960"
                                            >
                                                <path d="M360-200 80-480l280-280 56 56-183 184h647v80H233l184 184-57 56Z" />
                                            </svg>
                                            <div className="fs-4 fw-bold ms-1">{translate('pitcher-overview.sections.bat-tracking-late')}</div>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <div className="fs-4 fw-bold me-1">{translate('pitcher-overview.sections.bat-tracking-early')}</div>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                height="29px"
                                                viewBox="0 -960 960 960"
                                            >
                                                <path d="m600-200-57-56 184-184H80v-80h647L544-704l56-56 280 280-280 280Z" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-center d-flex flex-column justify-content-between align-items-center">
                                    <div className="fs-4 fw-bold">{translate('pitcher-overview.sections.bat-tracking-bat-locations')}</div>
                                    <BatImpactChart
                                        seriesData={batImpactSeries}
                                        isLhb={isLhb}
                                        tooltipFormatter={batImpactTooltipFormatter}
                                        pitchTypeLeagueAverages={pitchTypeLeagueAverages}
                                        zoom
                                        pitcherView={false}
                                        sizeMultiplier={0.8}
                                    />
                                </div>
                            </div>
                            <div className="d-flex justify-content-center align-items-center pb-2 mt-4 d-print-none">
                                {orderedPitchTypes.toReversed().map((pt) => (
                                    <div className="d-flex align-items-center mx-2" key={`${pt.fxtype}-${pt.fxsubtype}`}>
                                        <StyledCheckboxWrapper $color={pitchSubTypeColor(pt)}>
                                            <Checkbox
                                                id={`pitcher-overview-bat-tracking-${pitchTypeOrSubType(pt.fxsubtype || pt.fxtype)}`}
                                                checked={selectedPitchTypes
                                                    .some((spt) => spt.fxtype === pt.fxtype && spt.fxsubtype === pt.fxsubtype && spt.inz === null)}
                                                onClick={() => onPitchTypeClick(getPitchTypeSelection(pt, null))}
                                            />
                                        </StyledCheckboxWrapper>
                                        <Button variant="link" onClick={() => setSelectedPitchTypes([getPitchTypeSelection(pt, null)])} className="ps-0">
                                            {pitchTypeOrSubType(pt.fxsubtype || pt.fxtype, false, null, translate)}{' '}
                                            ({getPitchTypeTrackedSwings(pitches, pt, null)})
                                        </Button>
                                    </div>
                                ))}
                            </div>
                            <div className="d-flex justify-content-center align-items-center pb-2 mt-2 d-print-none">
                                {orderedPitchTypes.toReversed().map((pt) => (
                                    <div className="d-flex align-items-center mx-2" key={`${pt.fxtype}-${pt.fxsubtype}-inz`}>
                                        <StyledCheckboxWrapper $color={pitchSubTypeColor(pt)} $square>
                                            <Checkbox
                                                id={`pitcher-overview-bat-tracking-${pitchTypeOrSubType(pt.fxsubtype || pt.fxtype)}-inz`}
                                                checked={selectedPitchTypes
                                                    .some((spt) => spt.fxtype === pt.fxtype && spt.fxsubtype === pt.fxsubtype && spt.inz === 1)}
                                                onClick={() => onPitchTypeClick(getPitchTypeSelection(pt, 1))}
                                            />
                                        </StyledCheckboxWrapper>
                                        <Button variant="link" onClick={() => setSelectedPitchTypes([getPitchTypeSelection(pt, 1)])} className="ps-0">
                                            {pitchTypeOrSubType(pt.fxsubtype || pt.fxtype, false, null, translate)} - InZ{' '}
                                            ({getPitchTypeTrackedSwings(pitches, pt, 1)})
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : <div className="d-flex justify-content-center py-5 text-muted">{translate('pitcher-overview.no-data')}</div>}
                </ReportCard>
            </Col>
        </Row>
    );
}
